import { CommonModule } from '@angular/common';
import { Component, computed, input, output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatLegacyTooltipModule as MatTooltipModule } from '@angular/material/legacy-tooltip';
import { TranslateModule } from '@ngx-translate/core';
import { DropdownItem } from 'components/dropdown/dropdown.model';
import { DropdownComponent } from 'components/dropdown/dropdown.component';
import { AvatarComponent } from 'components/avatar/avatar.component';
import { IconComponent } from 'components/icon/icon.component';
import { Company } from '@weavix/models/src/company/company';
import { Person } from 'weavix-shared/models/person.model';
import { MapWalt } from 'weavix-shared/models/weavix-map.model';
import { SimInfo, WifiInfo } from '@weavix/models/src/walt/walt-connect-info';
import { getWaltModel, WaltModel } from '@weavix/models/src/walt/walt';
import { WaltStatus, WaltStatusTranslation } from 'weavix-shared/models/walt.model';
import { Point } from 'weavix-shared/models/location.model';
import { MapUtils } from 'weavix-shared/utils/map';
import { WaltService } from 'weavix-shared/services/walt.service';
import { AutoUnsubscribe } from 'weavix-shared/utils/utils';
import { SvgIcon } from 'weavix-shared/models/icon.model';
import { formatCoordinates } from '../walt-location.utils';
import { poweredOffReasonKey } from '../powered-off-reason';
import { WaltSoundControlComponent } from '../walt-sound-control/walt-sound-control.component';
import { OverflowTooltipDirective } from './overflow-tooltip.directive';

/** One Cellular box: a SIM slot, its record, and its display state. */
interface CellularRow {
  slot: 1 | 2;
  sim: SimInfo | undefined;
  /** True when the SIM has a client connection. */
  connected: boolean;
  /** Active state when Dual SIM is on; undefined when it is off. */
  active: boolean | undefined;
  /** True when Dual SIM is on and the slot has no SIM (no iccid). */
  empty: boolean;
}

@AutoUnsubscribe()
@Component({
  selector: 'app-new-details',
  templateUrl: './new-details.component.html',
  styleUrls: ['./new-details.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    MatTooltipModule,
    AvatarComponent,
    IconComponent,
    DropdownComponent,
    WaltSoundControlComponent,
    OverflowTooltipDirective,
  ],
})
export class NewDetailsComponent {
  readonly walt = input.required<MapWalt>();
  readonly waltIcon = computed<{ svgFile: string }>(() => ({
      svgFile: this.waltModel() === WaltModel.walt1 ? SvgIcon.Walt1 : SvgIcon.Walt2,
}));
  readonly hasDeviceManagementEditPerms = input(false);
  readonly showOnSite = input(false);
  readonly useDualSims = input(false);
  readonly company = input<Company | null>(null);
  readonly waltFacility = input<string | undefined>(undefined);
  readonly facilityDropdown = input<DropdownItem[]>([]);
  readonly selectedCraft = input<string | undefined>(undefined);
  readonly craftsDropdown = input<DropdownItem[]>([]);
  readonly canLogOut = input(false);
  readonly person = input<Person | null>(null);
  readonly isRefreshing = input(false);
  /** True only when the walt is known to be behind the version it is required to run, so Update has something to install. */
  readonly needsRequiredUpdate = input(false);
  /** True when the enhancements flag is on for the walt's own site, so Update, Off and Restart can be sent. */
  readonly commandsEnabled = input(false);
  /** True once the current-session refresh ended without a healthy response. */
  readonly refreshFailed = input(false);
  /** Whether the walt is inside any known site, resolved by the parent (not just its assigned site). */
  readonly isOnSite = input(false);
  /** Geofence name, or a repeater pin's coordinates, to display in the Location row */
  readonly locationLabel = input<string | null>(null);
  /** Point the map zooms to when the Location row is clicked; the row is not clickable when null. */
  readonly mapLocation = input<Point | null>(null);

  readonly waltModel = computed(() => getWaltModel(this.walt().id));
  readonly waltStatus = computed(() => WaltService.getWaltStatus(this.walt(), true));
  /** The walt's most recent direct report, falling back to `updated` for older records. */
  readonly lastReportedAt = computed(() => WaltService.getLastReportedAt(this.walt()));
  /** True when the walt's most recent direct report is more than 24 hours old. */
  readonly isLastReportStale = computed(() => this.isOlderThanRecentWindow(WaltService.getLastReportedTimestamp(this.walt())));
  /** True when the reported location is more than 24 hours old; false when no location was ever reported. */
  readonly isLocationStale = computed(() => this.isOlderThanRecentWindow(new Date(this.walt().locationUpdated ?? 0).getTime()));
  readonly waltStatusLabel = computed(() => {
    const status = this.waltStatus();
    return status ? WaltStatusTranslation[status] : null;
  });
  /** Label key for why the walt powered off, or null unless its status is Powered Off; a missing or unrecognised reason is Unknown. */
  readonly poweredOffReasonKey = computed(() => this.waltStatus() === WaltStatus.PoweredOff ? poweredOffReasonKey(this.walt().poweredOffReason) : null);
  /** True when the walt is Powered Off because its battery died, so the battery shows in red. */
  readonly isDeadBattery = computed(() => this.waltStatus() === WaltStatus.PoweredOff && this.walt().poweredOffReason === 'battery');
  /** When the walt powered off, or null unless its status is Powered Off and the record holds a date. */
  readonly poweredOffAt = computed(() => {
    const poweredOff = this.walt().poweredOff;
    return this.waltStatus() === WaltStatus.PoweredOff && poweredOff && typeof poweredOff !== 'boolean' ? poweredOff : null;
  });
  /** True when the walt cannot take Ring, Update, Off or Restart: no edit permission, powered off, or no connection. */
  readonly actionsDisabled = computed(() =>
    !this.hasDeviceManagementEditPerms() || this.waltStatus() === WaltStatus.PoweredOff || this.waltStatus() === WaltStatus.NoConnection,
  );
  /** True when Off and Restart are unavailable: the actions are disabled or the flag is off for the walt's site. */
  readonly commandsDisabled = computed(() => this.actionsDisabled() || !this.commandsEnabled());
  /** True when Update is unavailable: Off and Restart are, or the walt is not known to be behind its required version. */
  readonly updateDisabled = computed(() => this.commandsDisabled() || !this.needsRequiredUpdate());
  /** Translation key for the location heading: "Last Known Location" when unreachable, powered off, or the refresh failed. */
  readonly locationTitleKey = computed(() =>
    this.waltStatus() === WaltStatus.NoConnection || this.waltStatus() === WaltStatus.PoweredOff || this.didRefreshFail()
      ? 'walt.last-known-location'
      : 'generics.location',
  );

  /** The Wi-Fi record when the walt is connected to a network, otherwise null. */
  readonly wifi = computed<WifiInfo | null>(() => {
    const network = this.walt().currentNetwork;
    return network?.clientId ? network : null;
  });
  /** Cellular boxes to render: one SIM when Dual SIM is off, both slots with the Active one first when on. */
  readonly cellularRows = computed<CellularRow[]>(() => {
    const { simOne, simTwo } = this.walt();
    const preferredSlot = this.pickPreferredSlot(simOne, simTwo);
    if (!this.useDualSims()) {
      const sim = preferredSlot === 2 ? simTwo : simOne;
      return [{ slot: preferredSlot, sim, connected: this.isSimConnected(sim), active: undefined, empty: false }];
    }
    const activeSlot = this.isSimConnected(preferredSlot === 2 ? simTwo : simOne) ? preferredSlot : undefined;
    const toRow = (slot: 1 | 2, sim: SimInfo | undefined): CellularRow => {
      const empty = !sim?.iccid;
      return { slot, sim, connected: this.isSimConnected(sim), active: !empty && activeSlot === slot, empty };
    };
    return [toRow(1, simOne), toRow(2, simTwo)].sort((a, b) => Number(b.active) - Number(a.active));
  });
  /** Text for the Location row: the geofence name, else coordinates for an off-site walt with a known location, else null. */
  readonly locationText = computed(() => {
    const label = this.locationLabel();
    if (label) return label;
    const location = this.walt().location;
    return !this.isOnSite() && MapUtils.isValidLocation(location) ? formatCoordinates(location) : null;
  });
  /** Fallback key for the Location row when no text resolves: "Never Reported" when the walt has no location on record, else "Unknown". */
  readonly locationFallbackKey = computed(() => this.walt().location ? 'generics.unknown' : 'walt.location-never-reported');

  readonly refreshWaltOutput = output<void>();
  readonly rebootWaltOutput = output<void>();
  readonly powerOffWaltOutput = output<void>();
  readonly installUpdateOutput = output<void>();
  /** Emitted when the clickable Location row is clicked. */
  readonly locationClickOutput = output<void>();
  readonly facilityOutput = output<DropdownItem>();
  readonly craftOutput = output<DropdownItem>();
  readonly remoteLogoutOutput = output<void>();
  readonly updatedDateFormat = 'MMM dd, h:mm a';
  readonly hoverDateFormat = 'MMM d, y, h:mm a';

  getWaltModelLabel(): string {
    return this.waltModel() === WaltModel.walt1 ? 'walt.walt1' : 'walt.walt2';
  }

  /** True when the current session's refresh failed, or the stored record already shows one did. */
  didRefreshFail(): boolean {
    return this.refreshFailed() || WaltService.hasRefreshFailureAfterLatestUpdate(this.walt());
  }

  /** True when the SIM record has a client connection. */
  private isSimConnected(sim: SimInfo | undefined): boolean {
    return !!sim?.clientId;
  }

  /** Slot of the connected SIM (latest connected if both), else the latest ever connected, else slot 1. */
  private pickPreferredSlot(simOne: SimInfo | undefined, simTwo: SimInfo | undefined): 1 | 2 {
    const time = (sim: SimInfo | undefined): number => {
      const value = sim?.clientConnectedDate ? new Date(sim.clientConnectedDate).getTime() : NaN;
      return Number.isNaN(value) ? -Infinity : value;
    };
    const slots: Array<{ slot: 1 | 2, sim: SimInfo | undefined }> = [
      { slot: 1, sim: simOne },
      { slot: 2, sim: simTwo },
    ];
    const connectedSlots = slots.filter(entry => this.isSimConnected(entry.sim));
    const candidates = connectedSlots.length ? connectedSlots : slots;
    return candidates.reduce((best, entry) => (time(entry.sim) > time(best.sim) ? entry : best), candidates[0]).slot;
  }

  /** True when an epoch-ms time is set, valid, and older than the 24-hour recent-update window. */
  private isOlderThanRecentWindow(timestamp: number): boolean {
    return timestamp > 0 && timestamp < Date.now() - WaltService.RECENT_UPDATE_TIME_HOURS * 60 * 60 * 1000;
  }

  isActiveStatus(): boolean {
    return this.waltStatus() === WaltStatus.Active;
  }

  isDocked(): boolean {
    return this.waltStatus() === WaltStatus.Docked;
  }

  /** True when the walt's status is No Connection, so its last battery reading is not trustworthy. */
  isNoConnection(): boolean {
    return this.waltStatus() === WaltStatus.NoConnection;
  }

  onRefreshWalt() {
    this.refreshWaltOutput.emit();
  }

  onRebootWalt() {
    this.rebootWaltOutput.emit();
  }

  onPowerOffWalt() {
    this.powerOffWaltOutput.emit();
  }

  onInstallUpdate() {
    this.installUpdateOutput.emit();
  }

  onFacilityOutput(selected: DropdownItem) {
    this.facilityOutput.emit(selected);
  }

  onCraftOutput(selected: DropdownItem) {
    this.craftOutput.emit(selected);
  }

  onRemoteLogout() {
    this.remoteLogoutOutput.emit();
  }
}
